import { useEffect, useMemo, useRef, useState } from 'react';
import { EmptyState } from '@/shared/ui';
import { buildPickerSrcDoc } from './iframePickerScript';
import { useIframePickerMessages } from './useIframePickerMessages';
import type { SectionPickerClickPayload } from './sectionPicker.types';
import { useTranslation } from 'react-i18next';

// The picker script is loaded as an external file to comply with CSP's script-src directive.
// Served from the app's own origin via Vite's public/ folder.
const SECTION_PICKER_SCRIPT_URL = '/section-picker.js';

interface SectionPickerIframeProps {
  htmlContent: string;
  onElementClick: (payload: SectionPickerClickPayload) => void;
  /** Whether picker interaction (hover/click) is enabled. Defaults to true. */
  pickerEnabled?: boolean;
  /** When true, iframe dynamically adjusts height to fit content (like AutoHeightIframe). */
  autoHeight?: boolean;
  /** When true, hides green outlines on tagged (data-updatable-*) sections. */
  hideTaggedHighlights?: boolean;
  /**
   * When true, hovering ANY element shows a badge pinned to its top-left with the section slug, or
   * `<tagname>` when untagged. Off by default so the email widget keeps its quieter tooltip, which
   * only appears for a tagged or locked element.
   */
  showElementLabels?: boolean;
  /**
   * Slug of a section to outline from outside the frame — the editor sets this while a row in its
   * contents list is hovered or focused. `null` clears it.
   */
  highlightedSection?: string | null;
}

export function SectionPickerIframe({
  htmlContent,
  onElementClick,
  pickerEnabled = true,
  autoHeight = false,
  hideTaggedHighlights = false,
  showElementLabels = false,
  highlightedSection = null,
}: SectionPickerIframeProps) {
  const { t } = useTranslation('common');

  useIframePickerMessages(onElementClick);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [height, setHeight] = useState<string>('0px');

  const pickerSrcDoc = useMemo(
    () =>
      buildPickerSrcDoc(htmlContent, {
        hideTaggedHighlights,
        scriptUrl: SECTION_PICKER_SCRIPT_URL,
      }),
    [htmlContent, hideTaggedHighlights],
  );

  // Send TOGGLE_PICKER message to iframe when pickerEnabled changes
  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    function sendToggle() {
      iframe?.contentWindow?.postMessage(
        { type: 'TOGGLE_PICKER', enabled: pickerEnabled, showElementLabels },
        '*',
      );
      if (autoHeight) {
        iframe?.contentWindow?.postMessage({ type: 'ENABLE_AUTO_HEIGHT' }, '*');
      }
    }

    function onLoad() {
      sendToggle();
    }

    if (iframe.contentWindow) {
      sendToggle();
    }
    iframe.addEventListener('load', onLoad);
    return () => iframe.removeEventListener('load', onLoad);
  }, [pickerEnabled, autoHeight, showElementLabels]);

  // Kept separate from the TOGGLE_PICKER effect so a hover can't re-toggle the picker.
  useEffect(() => {
    iframeRef.current?.contentWindow?.postMessage(
      { type: 'HIGHLIGHT_SECTION', sectionName: highlightedSection },
      '*',
    );
  }, [highlightedSection]);

  // Auto-height via postMessage — we can't access contentDocument because the iframe
  // has an opaque origin (sandbox without allow-same-origin, required to bypass CSP).
  // Instead, the iframe's picker script measures its own height and sends it here.
  useEffect(() => {
    if (!autoHeight) return;
    function handleResize(event: MessageEvent<unknown>) {
      if (event.origin !== 'null') return;
      const data = event.data as Record<string, unknown> | null;
      if (data?.type === 'SECTION_PICKER_RESIZE' && typeof data.height === 'number') {
        setHeight(`${data.height}px`);
      }
    }
    window.addEventListener('message', handleResize);
    return () => window.removeEventListener('message', handleResize);
  }, [autoHeight]);

  if (!htmlContent) {
    return (
      <EmptyState
        title={t('sectionPicker.sectionPickerIframe.noTemplateContentToDisplay')}
        height="100%"
        className="min-h-100"
      />
    );
  }

  return (
    <div className="overflow-hidden rounded border border-border bg-card">
      {/* sandbox="allow-scripts" (without allow-same-origin) gives the iframe an opaque origin.
          The picker script is loaded as an external file from the public-assets CDN (or locally
          from /section-picker.js) to comply with the deployed CSP's script-src directive. */}
      <iframe
        ref={iframeRef}
        srcDoc={pickerSrcDoc}
        sandbox="allow-scripts"
        title={t('sectionPicker.sectionPickerIframe.templatePreview')}
        className={`block w-full border-none ${autoHeight ? '' : 'min-h-125'}`}
        style={{ height: autoHeight ? height : '100vh' }}
      />
    </div>
  );
}
